'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import { warmGrey } from '@enlighten/design-system/dist/colors';
import Divider from '@enlighten/design-system/dist/Divider';
import Grid from '@enlighten/design-system/dist/Grid';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import Stack from '@enlighten/design-system/dist/Stack';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Timeline from '@enlighten/design-system/dist/Timeline';
import TimelineConnector from '@enlighten/design-system/dist/TimelineConnector';
import TimelineContent from '@enlighten/design-system/dist/TimelineContent';
import TimelineDot from '@enlighten/design-system/dist/TimelineDot';
import TimelineItem from '@enlighten/design-system/dist/TimelineItem';
import TimelineSeparator from '@enlighten/design-system/dist/TimelineSeparator';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import { Fragment, useEffect, useRef, useState } from 'react';

import ObserveView from '@/components/pages/company/ObserveView';
import { enFonts } from '@/styles/fonts';

const list = [
	{
		label: 2026,
		content: [
			'Signed an MOU with SK Eternix for renewable energy cooperation',
			"Received an 'A' grade in the preliminary technology assessment",
			'Secured 30 billion KRW in investment for rooftop solar facilities',
			'Obtained ISO 9001 and ISO 14001 certifications',
		],
	},
	{
		label: 2025,
		content: [
			'Selected as the RE100 on-site solar provider for tier-1 partners of Samsung Electronics',
			'Signed a 5MW PPA with KT&G',
			'Signed an MOU to promote RE100 adoption in industrial complexes in Chungcheongnam-do',
		],
	},
	{
		label: 2024,
		content: ["Secured South Korea's first project financing loan for the RE100 Solar Subscription", 'Signed an MOU to establish the Gyeonggi RE100 Trading Platform'],
	},
	{
		label: 2023,
		content: ['Signed an RE100 investment agreement with Gyeonggi-do Industrial Complex', 'Certification of Green Technology issued by the Ministry of SMEs and Startups'],
	},
	{
		label: 2022,
		content: ['Solar Champ achieved a 20% share in the domestic solar energy market', 'First in South Korea to achieve K-RE100 and Registered as a CF100 Partner'],
	},
	{
		label: 2021,
		content: ['Secured Series C investments', 'Established South Korea’s first rooftop solar fund (60 billion KRW)'],
	},
	{
		label: 2020,
		content: [
			'Certified as a renewable energy provider by the CDP (Carbon Disclosure Project) in Korea',
			'Designated as a ‘Global ICT Future Unicorn’ by the Ministry of Science and ICT',
			"Signed South Korea's first RE100 implementation plan",
		],
	},
	{
		label: 2019,
		content: ['Secured Series B investments', "Established South Korea's first dedicated equipment financing fund"],
	},
	{
		label: 2018,
		content: ['Secured Series A investments', 'Executed South Korea’s largest mid-sized solar and ESS project financing (KRW)'],
	},
	{
		label: 2017,
		content: ['Awarded “Energy Startup of the Year” by Korea Electric Power Corporation (KEPCO)'],
	},
	{
		label: 2016,
		content: ['Founded'],
	},
];

/** TimelineConnect 그라데이션 투명도 함수 */
function f(x: number, length: number) {
	return (Math.sin(((2 * Math.PI) / (length + 1)) * x - 0.5 * Math.PI) + 1) / 2;
}

interface HistoryItemProps {
	index: number;
	label: number;
	content: string[];
}

function HistoryItem({ index, label, content }: HistoryItemProps) {
	const [active, setActive] = useState(false);

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleScroll = () => {
			setActive((ref.current?.getBoundingClientRect().top || 0) <= 1000 && (ref.current?.getBoundingClientRect().top || 0) + (ref.current?.getBoundingClientRect().height || 0) > 1000);
		};
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<TimelineItem sx={{ gap: 10 }}>
			<TimelineSeparator>
				<TimelineDot
					variant="outlined"
					sx={{
						bgcolor: '#FFFFFF',
						border: `8px solid ${active ? '#7F3FFF' : warmGrey['700']}`,
						margin: 0,
						transition: 'border-color 0.3s',
					}}
				/>
				<TimelineConnector
					sx={{
						width: 6,
						background: `linear-gradient(rgba(255, 255, 255, ${f(index + 1, list.length).toFixed(8)}) 0%, rgba(255, 255, 255, ${f(index + 2, list.length).toFixed(8)}) 100%)`,
					}}
				/>
			</TimelineSeparator>
			<TimelineContent component="div" ref={ref}>
				<ObserveView delay={1000} justifyContent="flex-start">
					<Grid
						container
						alignItems="center"
						width="100%"
						borderTop={`2px solid ${active ? '#7F3FFF' : warmGrey['700']}`}
						marginBottom={3}
						paddingY={2}
						sx={{
							transition: 'border-top-color 0.3s',
						}}
					>
						<Typography fontSize={28} fontWeight={500} fontFamily={enFonts.style.fontFamily} letterSpacing={-0.1} color="common.white">
							{label}
						</Typography>
					</Grid>
					<List>
						{content.map((c, j) => (
							<ListItem
								key={j}
								sx={{
									alignItems: 'flex-start',
									color: 'common.white',
									fontSize: 22,
									letterSpacing: -0.1,
									lineHeight: 1.5,
									paddingY: 0,
									':before': {
										content: '"•"',
										marginRight: 1,
									},
								}}
							>
								{c}
							</ListItem>
						))}
					</List>
				</ObserveView>
			</TimelineContent>
		</TimelineItem>
	);
}

export default function History() {
	const [activeIndex, setActiveIndex] = useState<number>(0);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	useEffect(() => {
		setActiveIndex(0);
	}, [isMobile]);

	useEffect(() => {
		if (scrollContainerRef.current) {
			const buttonWidth = 96; // 버튼의 가로 크기 (CSS와 일치해야 함)
			const scrollPosition = activeIndex * (buttonWidth + 20); // 이동할 스크롤 위치 계산
			scrollContainerRef.current.scrollTo({
				left: scrollPosition,
				behavior: 'smooth', // 부드러운 스크롤
			});
		}
	}, [activeIndex]);

	return (
		<Stack width="100%" maxWidth={1440} padding={{ xs: '60px 0', md: '90px 24px 120px' }}>
			<Typography
				fontSize={{ xs: 26, md: 54 }}
				fontWeight={700}
				fontStyle="italic"
				textAlign="center"
				letterSpacing={-0.1}
				color="common.white"
				marginX={{ xs: 3, md: 0 }}
				marginBottom={{ xs: 5, md: '60px' }}
			>
				History
			</Typography>
			<Box display={{ xs: 'none', md: 'block' }}>
				<Timeline position="alternate-reverse">
					<TimelineItem>
						<TimelineSeparator>
							<TimelineConnector
								sx={{
									width: 6,
									background: `linear-gradient(rgba(255, 255, 255, ${f(0, list.length)}) 0%, rgba(255, 255, 255, ${f(1, list.length)}) 100%)`,
								}}
							/>
						</TimelineSeparator>
						<TimelineContent height={60} />
					</TimelineItem>
					{list.map(({ label, content }, i) => (
						<HistoryItem key={i} index={i} label={label} content={content} />
					))}
				</Timeline>
			</Box>
			<Box position="relative" display={{ xs: 'flex', md: 'none' }} width="100%" paddingBottom={2}>
				<Box
					ref={scrollContainerRef}
					display="flex"
					gap="20px"
					paddingX={3}
					sx={{
						overflowX: 'scroll',
						'::-webkit-scrollbar': {
							display: 'none',
						},
					}}
				>
					{list.map(({ label }, i) => (
						<Box key={i}>
							<Button
								variant="text"
								sx={{ padding: 0 }}
								onClick={() => {
									setActiveIndex(i);
								}}
							>
								<Typography fontSize={36} fontWeight={400} fontFamily={enFonts.style.fontFamily} letterSpacing={-0.1} color={activeIndex === i ? 'common.white' : warmGrey['700']} width={96}>
									{label}
								</Typography>
							</Button>
						</Box>
					))}
				</Box>
				{activeIndex < list.length - 1 && (
					<Box
						position="absolute"
						top={0}
						right={-24}
						width="20%"
						height="100%"
						sx={{
							background: 'linear-gradient(to right, #00000000 0%, #000000 100%)',
						}}
					/>
				)}
			</Box>
			<Divider sx={{ display: { xs: 'block', md: 'none' }, opacity: 1, borderColor: '#7F3FFF', marginX: 3 }} />
			{list.map(({ content }, i) =>
				activeIndex === i ? (
					<ObserveView key={i} triggerOnce={false} justifyContent="flex-start">
						<List
							sx={{
								display: { xs: 'block', md: 'none' },
								marginX: 3,
								paddingTop: 3,
							}}
						>
							{content.map((c, j) => (
								<ListItem
									key={j}
									sx={{
										alignItems: 'flex-start',
										padding: 0,
										':before': {
											content: '"•"',
											color: 'common.white',
											marginRight: 1,
										},
									}}
								>
									<Typography fontSize={14} fontWeight={400} letterSpacing={-0.1} color="common.white">
										{c}
									</Typography>
								</ListItem>
							))}
						</List>
					</ObserveView>
				) : (
					<Fragment key={i} />
				)
			)}
		</Stack>
	);
}
